import { useMemo, useState } from "react";

import { useProjectComponents } from "../../hooks/useComponents";
import type { ComponentRecord, ComponentType } from "../../types/components";
import { BrowseCatalogModal } from "./BrowseCatalogModal";
import { ComponentCard } from "./ComponentCard";
import { ComponentDetailPanel } from "./ComponentDetailPanel";

interface Props {
  projectId: string;
  componentType: ComponentType;
  onChangeType: (next: ComponentType) => void;
}

/**
 * Components workflow step — Form mode per ``docs/ui-architecture.md`` §4.2.
 * No canvas, no map. Left column: browser of the tenant's linked
 * component pool. Right column: spec sheet + provenance + actions for
 * the selected component. Trackers sub-tab is read-only in v1 (the
 * v1.1 empty state explains why).
 */
export function ComponentsWorkflowStep({
  projectId,
  componentType,
  onChangeType,
}: Props) {
  const { data: linked, isLoading } = useProjectComponents(
    projectId,
    componentType,
  );
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [showCatalog, setShowCatalog] = useState(false);

  const selected: ComponentRecord | null = useMemo(() => {
    if (!linked || !selectedId) return null;
    return linked.find((c) => c.id === selectedId) ?? null;
  }, [linked, selectedId]);

  return (
    <section
      data-testid="components-workflow-step"
      style={{
        display: "grid",
        gridTemplateColumns: "minmax(0, 1.2fr) minmax(0, 1.5fr)",
        gap: "1rem",
      }}
    >
      <div data-testid="component-browser">
        <SubTabs componentType={componentType} onChange={onChangeType} />

        <div style={{ marginTop: "1rem", display: "flex", gap: "0.5rem" }}>
          <button
            type="button"
            data-testid="browse-catalog-open"
            onClick={() => setShowCatalog(true)}
            style={btnSecondary}
          >
            Browse catalog
          </button>
          <button
            type="button"
            data-testid="lock-components-cta"
            disabled
            title="Available once at least one component is selected (Stage 10 wiring)."
            style={btnPrimary}
          >
            Lock components for this Study
          </button>
        </div>

        {componentType === "tracker" && (
          <p
            data-testid="trackers-v1-1-callout"
            style={{
              marginTop: "1rem",
              padding: "0.75rem 1rem",
              background: "#fef3c7",
              borderRadius: 6,
              color: "#92400e",
              fontSize: "0.85rem",
            }}
          >
            Trackers are seed-only in v1. Datasheet extraction for tracker
            SKUs lands in v1.1.
          </p>
        )}

        {isLoading && <div role="status">Loading components…</div>}
        {linked && linked.length === 0 && (
          <p
            data-testid="component-browser-empty"
            style={{ marginTop: "1rem", color: "#6b7280" }}
          >
            No {componentType}s selected yet. Browse the catalog or upload a
            datasheet on the Files tab to extract a new one.
          </p>
        )}
        <div style={{ marginTop: "1rem" }}>
          {linked?.map((c) => (
            <ComponentCard
              key={c.id}
              component={c}
              selected={c.id === selectedId}
              onSelect={() => setSelectedId(c.id)}
            />
          ))}
        </div>
      </div>

      <ComponentDetailPanel projectId={projectId} component={selected} />

      {showCatalog && (
        <BrowseCatalogModal
          projectId={projectId}
          componentType={componentType}
          onClose={() => setShowCatalog(false)}
        />
      )}
    </section>
  );
}

function SubTabs({
  componentType,
  onChange,
}: {
  componentType: ComponentType;
  onChange: (next: ComponentType) => void;
}) {
  const tabs: { type: ComponentType; label: string }[] = [
    { type: "module", label: "Modules" },
    { type: "inverter", label: "Inverters" },
    { type: "tracker", label: "Trackers" },
  ];
  return (
    <nav
      role="tablist"
      data-testid="component-subtabs"
      style={{
        display: "flex",
        gap: "0.5rem",
        borderBottom: "1px solid #e5e7eb",
      }}
    >
      {tabs.map((tab) => {
        const active = componentType === tab.type;
        return (
          <button
            key={tab.type}
            type="button"
            role="tab"
            aria-selected={active}
            data-testid={`component-subtab-${tab.type}`}
            onClick={() => onChange(tab.type)}
            style={{
              padding: "0.5rem 0.75rem",
              border: "none",
              background: "transparent",
              cursor: "pointer",
              fontWeight: active ? 600 : 400,
              borderBottom: active
                ? "2px solid #2563eb"
                : "2px solid transparent",
            }}
          >
            {tab.label}
          </button>
        );
      })}
    </nav>
  );
}

const btnPrimary: React.CSSProperties = {
  background: "#2563eb",
  color: "white",
  padding: "0.5rem 0.9rem",
  border: "none",
  borderRadius: 6,
  cursor: "not-allowed",
  opacity: 0.55,
};

const btnSecondary: React.CSSProperties = {
  background: "transparent",
  color: "#1e3a8a",
  padding: "0.5rem 0.9rem",
  border: "1px solid #93c5fd",
  borderRadius: 6,
  cursor: "pointer",
};
